import { useReducer } from "react"
import { Box, Stack, Tooltip, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowRotateRight } from "@fortawesome/sharp-regular-svg-icons"

import { useAppLocalization } from "../../../modules/localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
import { useDateTime } from "../../../modules/localization/useDateTime"
import { useInterval } from "../../hooks/useInterval"

import { LoadingIconButton } from "../buttons/LoadingButton"

const JUST_NOW_THRESHOLD_SECONDS = 60
const LAST_UPDATED_TICK_MS = 10 * 1000

type StyleProps = Readonly<{ bordered: boolean }>

const useStyles = makeStyles<StyleProps>()((theme, { bordered }) => ({
    button: {
        padding: ".75rem",
        borderRadius: ".75rem",
        color: theme.palette.action.active,
        width: "2.5rem",
        height: "2.5rem",
        ...(bordered && {
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.common.white,
            color: theme.palette.common.black,
        }),
    },
    buttonError: {
        color: theme.palette.tailwind.red[600],
        ...(bordered && {
            borderColor: theme.palette.tailwind.red[200],
        }),
    },
    lastUpdated: {
        color: theme.palette.tailwind.slate[500],
        whiteSpace: "nowrap",
    },
}))

type LastUpdatedLabelProps = Readonly<{
    updatedAt: number
    className: string
}>

function LastUpdatedLabel({ updatedAt, className }: LastUpdatedLabelProps): JSX.Element {
    const dateTimeFactory = useDateTime()
    const [, tick] = useReducer((count: number) => count + 1, 0)

    useInterval(tick, LAST_UPDATED_TICK_MS)

    const updated = dateTimeFactory.fromMillis(updatedAt)
    const isJustNow = dateTimeFactory.now().diff(updated).as("seconds") < JUST_NOW_THRESHOLD_SECONDS
    // "narrow" renders as "-3 min" in nb/sv
    const relativeTime = updated.toRelative({ style: "short" }) ?? ""

    return isJustNow ? (
        <LocalizedStrict id="standard-refresh-button-last-updated-just-now-label">
            <Typography variant="body2" className={className}>
                Updated just now
            </Typography>
        </LocalizedStrict>
    ) : (
        <LocalizedStrict id="standard-refresh-button-last-updated-label" vars={{ relativeTime }}>
            <Typography variant="body2" className={className}>
                Updated {relativeTime}
            </Typography>
        </LocalizedStrict>
    )
}

type RefreshableQueryResult = Readonly<{
    dataUpdatedAt: number
    isRefetching: boolean
    isError: boolean
    refetch: () => void
}>

export type StandardRefreshButtonProps = Readonly<{
    query: RefreshableQueryResult
    bordered?: boolean
    labelPlacement?: "start" | "end"
    className?: string
}>

export function StandardRefreshButton({
    query,
    bordered = false,
    labelPlacement = "end",
    className,
}: StandardRefreshButtonProps): JSX.Element | null {
    const { classes, cx } = useStyles({ bordered })
    const { l10n } = useAppLocalization()

    const hasLoaded = query.dataUpdatedAt > 0

    if (!hasLoaded && !query.isError) return null

    const tooltip = l10n.getString(
        query.isError ? "standard-refresh-button-error-tooltip" : "standard-refresh-button-tooltip-hint",
    )

    return (
        <Stack direction={labelPlacement === "start" ? "row-reverse" : "row"} alignItems="center" gap=".75rem">
            <Tooltip title={tooltip} placement="bottom-start">
                <Box component="span">
                    <LoadingIconButton
                        loading={query.isRefetching}
                        onClick={() => query.refetch()}
                        aria-label={l10n.getString("standard-refresh-button-aria-label")}
                        className={cx(classes.button, query.isError && classes.buttonError, className)}
                    >
                        <FontAwesomeIcon icon={faArrowRotateRight} size="xs" />
                    </LoadingIconButton>
                </Box>
            </Tooltip>

            {hasLoaded && <LastUpdatedLabel updatedAt={query.dataUpdatedAt} className={classes.lastUpdated} />}
        </Stack>
    )
}
